import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { createUISlice, type UISlice } from './slices/uiSlice';
import { createProjectSlice, type ProjectSlice } from './slices/projectSlice';
import { createCalculationSlice, type CalculationSlice } from './slices/calculationSlice';
import { createHistorySlice, type HistorySlice } from './slices/historySlice';
import { createTriangleSlice, type TriangleSlice } from './slices/triangleSlice';
import { createRiskSlice, type RiskSlice } from './slices/riskSlice';
import { createSwotSlice, type SwotSlice } from './slices/swotSlice';
import { createKanbanSlice, type KanbanSlice } from './slices/kanbanSlice';
import { createAlertSlice, type AlertSlice } from './slices/alertSlice';
import { createClosureSlice, type ClosureSlice } from './slices/closureSlice';
import { createSpecificationSlice, type SpecificationSlice } from './slices/specificationSlice';
import { addWorkingDays } from '../utils/calendar';
import { ALERT_RULES } from '../utils/alertRules';
import { reviveProject } from './utils/project';

import type { ProjectStatistics, SearchResult, StorageMode, Project, GlobalRole } from '../types';

const ENV_STORAGE_MODE = (import.meta.env.VITE_STORAGE_MODE as StorageMode | undefined) || null;
const ENABLE_BACKEND = import.meta.env.VITE_ENABLE_BACKEND === 'true';
const DEFAULT_STORAGE_MODE: StorageMode =
  ENV_STORAGE_MODE === 'local' || ENV_STORAGE_MODE === 'backend'
    ? ENV_STORAGE_MODE
    : ENABLE_BACKEND
      ? 'backend'
      : 'local';

// ============================================================================
// MAIN STORE - Combines all slices with middleware
// ============================================================================

export type ProjectStore = UISlice &
  ProjectSlice &
  CalculationSlice &
  HistorySlice &
  TriangleSlice &
  RiskSlice &
  SwotSlice &
  KanbanSlice &
  AlertSlice &
  ClosureSlice &
  SpecificationSlice & {
    // Additional computed/utility methods

    // Statistics
    getStatistics: () => ProjectStatistics;

    // Search
    search: (query: string) => SearchResult[];

    // Persistence
    saveToLocalStorage: () => void;
    loadFromLocalStorage: () => boolean;
    storageMode: StorageMode;
    setStorageMode: (mode: StorageMode) => void;

    // Export/Import
    exportToJSON: () => string;
    importFromJSON: (json: string) => boolean;
    exportWorkspace: () => string;
    importWorkspace: (json: string) => boolean;

    // PERT Calculation (will integrate CPM algorithm in Phase 2)
    calculatePert: () => void;

    // Batch operations
    deleteSelected: () => void;
    duplicateSelected: () => void;
  };

export const useProjectStore = create<ProjectStore>()(
  devtools(
    persist(
      (set, get, api) => ({
        // Combine all slices
        ...createUISlice(set, get, api),
        ...createProjectSlice(set, get, api),
        ...createCalculationSlice(set, get, api),
        ...createHistorySlice(set, get, api),
        ...createTriangleSlice(set, get, api),
        ...createRiskSlice(set, get, api),
        ...createSwotSlice(set, get, api),
        ...createKanbanSlice(set, get, api),
        ...createAlertSlice(set, get, api),
        ...createClosureSlice(set, get, api),
        ...createSpecificationSlice(set, get, api),

        // Storage mode
        storageMode: DEFAULT_STORAGE_MODE,
        setStorageMode: (mode) => {
          set({ storageMode: mode });
          if (mode === 'backend') {
            get()
              .syncWithBackend?.()
              .catch((error) => {
                console.error('Backend sync failed', error);
                get().addNotification?.(
                  'error',
                  'Impossible de synchroniser les projets distants'
                );
              });
          }
        },

        // Statistics
        getStatistics: () => {
          const { nodes, edges, currentProject } = get();
          const criticalNodes = nodes.filter((n) => n.isCritical);
          const criticalEdges = edges.filter((e) => e.isCritical);

          const completedTasks = nodes.filter((n) => (n.progress || 0) === 100).length;
          const completionPercentage =
            nodes.length > 0 ? (completedTasks / nodes.length) * 100 : 0;

          const criticalPercentage =
            nodes.length > 0 ? (criticalNodes.length / nodes.length) * 100 : 0;

          const totalSlack = nodes.reduce((sum, n) => sum + n.slack, 0);
          const averageSlack = nodes.length > 0 ? totalSlack / nodes.length : 0;

          const totalDuration = nodes.length > 0 ? Math.max(...nodes.map((n) => n.earlyFinish || 0)) : 0;
          const startDate = currentProject?.startDate ? new Date(currentProject.startDate) : null;
          const includeWeekends = currentProject?.settings?.includeWeekends ?? true;
          const holidays = [...(currentProject?.settings?.holidays ?? [])];
          const projectedEndDate =
            startDate && totalDuration > 0
              ? addWorkingDays(startDate, totalDuration, includeWeekends, holidays)
              : startDate;
          const calendarDuration =
            startDate && projectedEndDate
              ? Math.max(
                  0,
                  Math.round(
                    (projectedEndDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
                  )
                )
              : totalDuration;

          return {
            totalNodes: nodes.length,
            totalEdges: edges.length,
            criticalNodes: criticalNodes.length,
            criticalEdges: criticalEdges.length,
            totalDuration,
            completionPercentage,
            criticalPercentage,
            averageSlack,
            completedTasks,
            totalTasks: nodes.length,
            criticalTasks: criticalNodes.length,
            criticalPathLength: get().criticalPath?.length || 0,
            resourceUtilization: 0,
            projectStartDate: startDate,
            projectedEndDate,
            calendarDuration,
            includeWeekends,
            holidays,
          };
        },

        // Search
        search: (query) => {
          const { nodes, edges, members } = get();
          const results: SearchResult[] = [];

          if (query.length < 2) return results;

          const lowerQuery = query.toLowerCase();

          // Search nodes
          nodes.forEach((node) => {
            if (
              node.name.toLowerCase().includes(lowerQuery) ||
              node.description?.toLowerCase().includes(lowerQuery)
            ) {
              results.push({
                type: 'node',
                id: node.id,
                title: node.name,
                subtitle: `Node • ${node.type} • ${node.isCritical ? 'Critical' : 'Normal'}`,
              });
            }
          });

          // Search edges
          edges.forEach((edge) => {
            if (
              edge.taskName.toLowerCase().includes(lowerQuery) ||
              edge.description?.toLowerCase().includes(lowerQuery)
            ) {
              results.push({
                type: 'edge',
                id: edge.id,
                title: edge.taskName,
                subtitle: `Task • ${edge.duration} days • ${edge.isCritical ? 'Critical' : 'Normal'}`,
              });
            }
          });

          // Search members
          members.forEach((member) => {
            if (
              member.name.toLowerCase().includes(lowerQuery) ||
              member.email.toLowerCase().includes(lowerQuery) ||
              member.role.toLowerCase().includes(lowerQuery)
            ) {
              results.push({
                type: 'member',
                id: member.id,
                title: member.name,
                subtitle: `${member.role} • ${member.email}`,
              });
            }
          });

          return results;
        },

        // LocalStorage persistence
        saveToLocalStorage: () => {
          const { currentProject, projects } = get();
          try {
            localStorage.setItem('projects', JSON.stringify(projects));
            if (currentProject) {
              localStorage.setItem('currentProjectId', currentProject.id);
            }
            get().addNotification('success', 'Projet sauvegardé en local');
          } catch (error) {
            get().addNotification('error', 'Impossible de sauvegarder en local');
          }
        },

        loadFromLocalStorage: () => {
          try {
            const projectsJson = localStorage.getItem('projects');
            const currentProjectId = localStorage.getItem('currentProjectId');

            if (projectsJson) {
              const rawProjects = JSON.parse(projectsJson) as Project[];
              const projects = rawProjects.map(reviveProject);
              const currentProject = currentProjectId
                ? projects.find((p) => p.id === currentProjectId) || null
                : null;

              set({
                projects,
                currentProject,
                nodes: currentProject?.nodes || [],
                edges: currentProject?.edges || [],
                members: currentProject?.members || [],
              });

              get().addNotification('success', 'Projet chargé depuis le stockage local');
              return true;
            }
            return false;
          } catch (error) {
            get().addNotification('error', 'Impossible de charger le stockage local');
            return false;
          }
        },

        // Export to JSON
        exportToJSON: () => {
          const { currentProject, nodes, edges, members } = get();

          const exportData = {
            version: '1.0',
            exportedAt: new Date().toISOString(),
            project: currentProject
              ? {
                  ...currentProject,
                  nodes,
                  edges,
                  members,
                }
              : null,
          };

          return JSON.stringify(exportData, null, 2);
        },

        // Import from JSON
        importFromJSON: (json) => {
          try {
            const data = JSON.parse(json);

            if (data.project) {
              const revived = reviveProject(data.project);
              set({
                currentProject: revived,
                nodes: revived.nodes || [],
                edges: revived.edges || [],
                members: revived.members || [],
              });

              get().addNotification('success', 'Projet importé avec succès');
              return true;
            }

            get().addNotification('error', 'Données de projet invalides');
            return false;
          } catch (error) {
            get().addNotification('error', 'Échec de l\'import du projet');
            return false;
          }
        },

        exportWorkspace: () => {
          const { projects, currentProject, storageMode } = get();
          const payload = {
            version: '1.0',
            exportedAt: new Date().toISOString(),
            storageMode,
            currentProjectId: currentProject?.id || null,
            projects,
          };
          return JSON.stringify(payload, null, 2);
        },

        importWorkspace: (json) => {
          try {
            const data = JSON.parse(json);
            if (!Array.isArray(data.projects)) {
              get().addNotification('error', 'Fichier de workspace invalide');
              return false;
            }

            const projects = data.projects.map((project: Project) => reviveProject(project));
            const currentProjectId: string | null = data.currentProjectId || null;
            const currentProject = currentProjectId
              ? projects.find((projectItem: Project) => projectItem.id === currentProjectId) || null
              : null;

            set({
              projects,
              currentProject,
              nodes: currentProject?.nodes || [],
              edges: currentProject?.edges || [],
              members: currentProject?.members || [],
              storageMode: data.storageMode || get().storageMode,
            });

            get().addNotification('success', 'Workspace importé avec succès');
            return true;
          } catch (error) {
            get().addNotification('error', 'Impossible d\'importer le workspace');
            return false;
          }
        },

        // PERT Calculation (integrated CPM algorithm)
        calculatePert: () => {
          const { nodes, edges } = get();

          if (nodes.length === 0) {
            get().addNotification('warning', 'Aucun nœud à calculer');
            return;
          }

          try {
            // Import and run CPM calculator
            import('../utils/cpm').then(({ calculateCpm }) => {
              const result = calculateCpm(nodes, edges);

              if (!result.isValid) {
                get().addNotification(
                  'error',
                  `Le calcul PERT a échoué : ${result.errors?.join(', ')}`
                );
                get().setCpmResult(result);
                return;
              }

              // Update store with calculated values
              set({
                nodes: result.nodes,
                edges: result.edges,
              });

              get().setCpmResult(result);

              // Show warnings if any
              if (result.warnings && result.warnings.length > 0) {
                get().addNotification('warning', `Avertissements : ${result.warnings.join(', ')}`);
              }

              get().addNotification(
                'success',
                `PERT calculé ! Durée totale : ${result.totalDuration} j, chemin critique : ${result.criticalPath.length} nœuds`
              );
            });
          } catch (error) {
            get().addNotification(
              'error',
              `Erreur de calcul PERT : ${error instanceof Error ? error.message : 'erreur inconnue'}`
            );
          }
        },

        // Batch operations
        deleteSelected: () => {
          const { selectedElements, nodes } = get();

          selectedElements.forEach((id) => {
            const isNode = nodes.find((n) => n.id === id);
            if (isNode) {
              get().deleteNode(id);
            } else {
              get().deleteEdge(id);
            }
          });

          get().clearSelection();
          get().addNotification('success', `${selectedElements.length} élément(s) supprimé(s)`);
        },

        duplicateSelected: () => {
          const { selectedElements, nodes } = get();

          let duplicatedCount = 0;

          selectedElements.forEach((id) => {
            const node = nodes.find((n) => n.id === id);
            if (node) {
              get().addNode({
                ...node,
                name: `${node.name} (Copy)`,
                x: node.x + 50,
                y: node.y + 50,
              });
              duplicatedCount++;
            }
          });

          if (duplicatedCount > 0) {
            get().addNotification('success', `${duplicatedCount} nœud(s) dupliqué(s)`);
          }
        },
      }),
      {
        name: 'project-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => {
          if (state.storageMode === 'backend') {
            return { storageMode: state.storageMode };
          }
          return {
            currentProject: state.currentProject,
            projects: state.projects,
            storageMode: state.storageMode,
          };
        },
        onRehydrateStorage: () => (state) => {
          if (!state) return;

          // Toujours appliquer le storageMode dérivé des variables d'environnement
          // (la valeur persistée peut être obsolète si le build a changé).
          // Cas typique : build précédent sans VITE_ENABLE_BACKEND → 'local' persisté,
          // nouveau build avec VITE_ENABLE_BACKEND=true → doit passer en 'backend'.
          if (state.storageMode !== DEFAULT_STORAGE_MODE) {
            state.storageMode = DEFAULT_STORAGE_MODE;
          }

          const project = state.currentProject;
          if (project && state.storageMode !== 'backend') {
            // Re-populate nodes/members/edges from the rehydrated project
            useProjectStore.setState({
              nodes: project.nodes ?? [],
              edges: project.edges ?? [],
              members: project.members ?? [],
            });
          }
        },
      }
    ),
    {
      name: 'ProjectStore',
    }
  )
);

// Initialize alert rules on store creation
useProjectStore.setState({ alertRules: ALERT_RULES });

// Export individual hooks for better tree-shaking (optional)
export const useNodes = () => useProjectStore((state) => state.nodes);
export const useEdges = () => useProjectStore((state) => state.edges);
export const useMembers = () => useProjectStore((state) => state.members);
export const useCurrentProject = () => useProjectStore((state) => state.currentProject);
export const useActiveView = () => useProjectStore((state) => state.activeView);
export const useNotifications = () => useProjectStore((state) => state.notifications);
export const useStatistics = () => useProjectStore((state) => state.getStatistics());

// ============================================================================
// AUTHENTICATION STORE
// ============================================================================

const AUTH_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';
const AUTH_TOKEN_KEY = 'project-suite-token';
const REFRESH_TOKEN_KEY = 'project-suite-refresh-token';
const REMEMBER_DEVICE_KEY = 'project-suite-mfa-remember';

// Tokens persistés dans localStorage — standard SPA pour auth cross-origin.
// Survit au refresh ET aux nouveaux onglets. Compatible Safari/Chrome/Firefox.
// Contourne les restrictions third-party cookies (Vercel ↔ Railway).
const LS_AUTH_KEY    = 'ps_mem_token';
const LS_REFRESH_KEY = 'ps_mem_refresh';

const readFromStorage = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};

const writeToStorage = (key: string, value: string | null): void => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch { /* ignore */ }
};

const readAuthToken = (): string | null => readFromStorage(LS_AUTH_KEY);

export const getAuthToken = (): string | null => readAuthToken();

const readRefreshToken = (): string | null => readFromStorage(LS_REFRESH_KEY);

export const getRefreshToken = (): string | null => readRefreshToken();

const readRememberToken = (): string | null => null;

const writeRememberToken = (_token: string | null) => {};

const writeAuthToken = (token: string | null): void => {
  writeToStorage(LS_AUTH_KEY, token);
};

const writeRefreshToken = (token: string | null): void => {
  writeToStorage(LS_REFRESH_KEY, token);
};

/**
 * Expose les writers de token pour que le flux d'inscription en deux temps
 * (register → org join → set user) puisse stocker le token AVANT de mettre
 * l'utilisateur en store (évite que AuthForm se démonte prématurément).
 */
export const setStoredAuthToken    = (token: string | null) => writeAuthToken(token);
export const setStoredRefreshToken = (token: string | null) => writeRefreshToken(token);

const clearLegacyAuthStorage = () => {
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(REMEMBER_DEVICE_KEY);
  } catch {
    // Ignore storage errors
  }
};

interface AuthRequestOptions extends RequestInit {
  skipAuth?: boolean;
  skipRefresh?: boolean;
  allowRefresh?: boolean;
}

const authRequest = async <T>(path: string, options: AuthRequestOptions = {}): Promise<T> => {
  const headers = new Headers(options.headers || {});
  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const token = readAuthToken();
  if (token && !options.skipAuth) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${AUTH_API_URL}${path}`, {
    ...options,
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
  });

  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    if (
      response.status === 401 &&
      !options.skipRefresh &&
      !options.skipAuth &&
      options.allowRefresh !== false
    ) {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        return authRequest<T>(path, { ...options, skipRefresh: true });
      }
    }
    const message =
      typeof payload === 'object' && payload !== null && 'message' in payload
        ? (payload as { message: string }).message
        : response.statusText || 'Une erreur est survenue';
    const error = new Error(message);
    if (payload && typeof payload === 'object') {
      (error as { payload?: unknown }).payload = payload;
    }
    throw error;
  }

  return payload as T;
};

const tryRefreshToken = async (): Promise<boolean> => {
  try {
    const rt = readRefreshToken();
    const result = await authRequest<{ token: string; refreshToken: string; user: RawAuthUser }>('/auth/refresh', {
      method: 'POST',
      skipAuth: true,
      skipRefresh: true,
      body: rt ? JSON.stringify({ refreshToken: rt }) : undefined,
    });
    writeAuthToken(result.token);
    writeRefreshToken(result.refreshToken);
    return true;
  } catch {
    writeAuthToken(null);
    writeRefreshToken(null);
    return false;
  }
};

/**
 * Tente de rafraîchir l'access token depuis le refresh token en cookie/localStorage.
 * Retourne `true` si le refresh a réussi (nouveau token en localStorage).
 * Utilisé par `authorizedRequest` dans src/api/projects.ts pour l'intercepteur 401.
 */
export const refreshAccessToken = tryRefreshToken;

type RawAuthUser = {
  id: string;
  email: string;
  name: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  role?: GlobalRole;
  globalRole?: GlobalRole;
  passwordResetRequired?: boolean;
  mfaEnabled?: boolean;
  organizationId?: string | null;
  orgMemberRole?: string | null;
  orgMemberStatus?: string | null;
};

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  username: string | null;
  avatarUrl: string | null;
  role: GlobalRole;
  globalRole: GlobalRole;
  passwordResetRequired: boolean;
  mfaEnabled: boolean;
  organizationId: string | null;
  orgMemberRole: string | null;
  orgMemberStatus: string | null;
}

// ─── In-app notification type ─────────────────────────────────────────────────
export interface InAppNotification {
  id: string;
  userId: string;
  type:
    | 'PROJECT_JOINED'
    | 'PROJECT_INVITATION'
    | 'PROJECT_ROLE_CHANGED'
    | 'PROJECT_REMOVED'
    | 'SYSTEM'
    | 'ORG_JOIN_REQUEST'
    | 'ORG_JOIN_APPROVED'
    | 'ORG_JOIN_REJECTED'
    | 'ORG_LICENSE_LOW'
    | 'ORG_PAYMENT_DUE';
  title: string;
  body: string;
  link: string | null;
  read: boolean;
  readAt: string | null;
  createdAt: string;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  refreshToken: string | null;
  isInitializing: boolean;
  isSubmitting: boolean;
  error: string | null;
  // In-app notifications
  inAppNotifications: InAppNotification[];
  inAppUnreadCount: number;
  fetchNotifications: () => Promise<void>;
  markNotificationRead: (id: string) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  addSocketNotification: (n: InAppNotification) => void;
  mfaRequired: boolean;
  mfaChallengeId: string | null;
  mfaMessage: string | null;
  temporaryMfaCode: string | null;
  mfaExpiresAt: string | null;
  mfaAttemptsRemaining: number | null;
  mfaLockedUntil: string | null;
  mfaResendAvailableAt: string | null;
  rememberDevice: boolean;
  initialize: () => Promise<void>;
  refreshUser: () => Promise<void>;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  register: (payload: { email: string; password: string; name: string }) => Promise<void>;
  logout: () => void;
  updateProfile: (payload: { name?: string; username?: string; phoneNumber?: string }) => Promise<void>;
  uploadAvatar: (file: File) => Promise<void>;
  removeAvatar: () => Promise<void>;
  toggleMfa: (payload: { enable: boolean; password: string }) => Promise<void>;
  changePassword: (payload: { currentPassword: string; newPassword: string }) => Promise<void>;
  resetPasswordWithTemp: (email: string, tempPassword: string, newPassword: string) => Promise<void>;
  requestPasswordReset: (payload: {
    email: string;
    channel?: 'email' | 'sms';
  }) => Promise<{
    message: string;
    temporaryPassword: string | null;
    channel?: string;
    resendAvailableAt?: string;
  }>;
  verifyMfa: (payload: { challengeId: string; code: string }) => Promise<void>;
  resendMfa: (payload: { challengeId: string }) => Promise<void>;
  setRememberDevice: (value: boolean) => void;
  clearMfa: () => void;
  enterGuestMode: () => void;
}

const handleAuthErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Une erreur est survenue';

const normalizeAuthUser = (user: RawAuthUser): AuthUser => {
  const normalizedRole = user.globalRole ?? user.role ?? 'MEMBER';
  return {
    id: user.id,
    email: user.email,
    name: user.name ?? null,
    username: user.username ?? null,
    avatarUrl: user.avatarUrl ?? null,
    role: normalizedRole,
    globalRole: normalizedRole,
    passwordResetRequired: Boolean(user.passwordResetRequired),
    mfaEnabled: Boolean(user.mfaEnabled),
    organizationId: user.organizationId ?? null,
    orgMemberRole: user.orgMemberRole ?? null,
    orgMemberStatus: user.orgMemberStatus ?? null,
  };
};

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  token: null,
  refreshToken: null,
  isInitializing: true,
  isSubmitting: false,
  error: null,
  inAppNotifications: [],
  inAppUnreadCount: 0,

  fetchNotifications: async () => {
    try {
      const { notifications, unreadCount } = await authRequest<{
        notifications: InAppNotification[];
        unreadCount: number;
      }>('/notifications');
      set({ inAppNotifications: notifications, inAppUnreadCount: unreadCount });
    } catch {
      // silencieux — pas critique
    }
  },

  markNotificationRead: async (id) => {
    try {
      await authRequest(`/notifications/${id}/read`, { method: 'PATCH' });
      set((s) => ({
        inAppNotifications: s.inAppNotifications.map((n) =>
          n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n
        ),
        inAppUnreadCount: Math.max(0, s.inAppUnreadCount - 1),
      }));
    } catch { /* silencieux */ }
  },

  markAllNotificationsRead: async () => {
    try {
      await authRequest('/notifications/read-all', { method: 'PATCH' });
      set((s) => ({
        inAppNotifications: s.inAppNotifications.map((n) => ({
          ...n, read: true, readAt: new Date().toISOString(),
        })),
        inAppUnreadCount: 0,
      }));
    } catch { /* silencieux */ }
  },

  deleteNotification: async (id) => {
    try {
      await authRequest(`/notifications/${id}`, { method: 'DELETE' });
      set((s) => {
        const removed = s.inAppNotifications.find((n) => n.id === id);
        return {
          inAppNotifications: s.inAppNotifications.filter((n) => n.id !== id),
          inAppUnreadCount: removed && !removed.read
            ? Math.max(0, s.inAppUnreadCount - 1)
            : s.inAppUnreadCount,
        };
      });
    } catch { /* silencieux */ }
  },

  clearAllNotifications: async () => {
    try {
      await authRequest('/notifications', { method: 'DELETE' });
      set({ inAppNotifications: [], inAppUnreadCount: 0 });
    } catch { /* silencieux */ }
  },

  addSocketNotification: (n) => {
    set((s) => ({
      inAppNotifications: [n, ...s.inAppNotifications],
      inAppUnreadCount: s.inAppUnreadCount + 1,
    }));
  },

  mfaRequired: false,
  mfaChallengeId: null,
  mfaMessage: null,
  temporaryMfaCode: null,
  mfaExpiresAt: null,
  mfaAttemptsRemaining: null,
  mfaLockedUntil: null,
  mfaResendAvailableAt: null,
  rememberDevice: false,

  initialize: async () => {
    clearLegacyAuthStorage();
    if (get().user?.globalRole === 'GUEST' && get().user?.id === 'guest-local') {
      set({ isInitializing: false });
      return;
    }
    try {
      // Utiliser authRequest pour inclure automatiquement le Bearer token
      // stocké dans localStorage (nécessaire depuis la migration cookie → Bearer).
      const payload = await authRequest<{ user: RawAuthUser }>('/auth/me', {
        skipRefresh: true,
      });
      const user = payload?.user as RawAuthUser | undefined;
      if (!user) {
        set({ user: null, token: null, refreshToken: null, isInitializing: false });
        return;
      }
      set({
        user: normalizeAuthUser(user),
        token: null,
        refreshToken: null,
        isInitializing: false,
      });
    } catch (error) {
      // 401 ou toute autre erreur réseau → session invalide
      console.warn('Session invalide, réinitialisation...');
      writeAuthToken(null);
      writeRefreshToken(null);
      set({ user: null, token: null, refreshToken: null, isInitializing: false });
    }
  },

  // Rafraîchit uniquement le profil utilisateur (orgMemberRole, etc.) sans toucher aux tokens
  refreshUser: async () => {
    try {
      const payload = await authRequest<{ user: RawAuthUser }>('/auth/me', { skipRefresh: true });
      const user = payload?.user as RawAuthUser | undefined;
      if (user) set({ user: normalizeAuthUser(user) });
    } catch { /* silencieux — la session reste valide */ }
  },

  login: async (credentials) => {
    set({ isSubmitting: true, error: null });
    try {
      const result = await authRequest<
        | { token: string; refreshToken: string; user: RawAuthUser }
        | {
            mfaRequired: true;
            challengeId: string;
            user: RawAuthUser;
            message?: string;
            temporaryCode?: string;
            otpExpiresAt?: string | null;
            attemptsRemaining?: number | null;
            lockedUntil?: string | null;
            resendAvailableAt?: string | null;
          }
      >('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ ...credentials, rememberToken: readRememberToken() }),
        skipAuth: true,
      });
      if ('mfaRequired' in result && result.mfaRequired) {
        set({
          isSubmitting: false,
          mfaRequired: true,
          mfaChallengeId: result.challengeId,
          mfaMessage: result.message ?? null,
          temporaryMfaCode: result.temporaryCode ?? null,
          mfaExpiresAt: result.otpExpiresAt ?? null,
          mfaAttemptsRemaining:
            typeof result.attemptsRemaining === 'number' ? result.attemptsRemaining : null,
          mfaLockedUntil: result.lockedUntil ?? null,
          mfaResendAvailableAt: result.resendAvailableAt ?? null,
        });
        return;
      }
      if (!('token' in result)) {
        throw new Error('Réponse de connexion invalide');
      }
      // Persister le token dans localStorage AVANT la mise à jour du store
      // pour que les appels API suivants (useBackendPersistence, syncWithBackend…)
      // disposent immédiatement du header Authorization.
      writeAuthToken(result.token);
      writeRefreshToken(result.refreshToken);
      set({
        user: normalizeAuthUser(result.user),
        token: null,        // Le token vit dans localStorage, pas dans le store
        refreshToken: null, // pour éviter l'exposition dans les DevTools Zustand
        isSubmitting: false,
        mfaRequired: false,
        mfaChallengeId: null,
        mfaMessage: null,
        temporaryMfaCode: null,
        mfaExpiresAt: null,
        mfaAttemptsRemaining: null,
        mfaLockedUntil: null,
        mfaResendAvailableAt: null,
      });
      // Rafraîchir le profil complet via /auth/me pour s'assurer que
      // orgMemberRole/orgMemberStatus sont à jour (sécurité contre toute
      // race condition si la réponse login est incomplète).
      await get().refreshUser();
    } catch (error) {
      set({ error: handleAuthErrorMessage(error), isSubmitting: false });
    }
  },

  register: async (payload) => {
    set({ isSubmitting: true, error: null });
    try {
      const { token, refreshToken, user } = await authRequest<{
        token: string;
        refreshToken: string;
        user: RawAuthUser;
      }>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(payload),
        skipAuth: true,
      });
      writeAuthToken(token);
      writeRefreshToken(refreshToken);
      set({ user: normalizeAuthUser(user), token: null, refreshToken: null, isSubmitting: false });
    } catch (error) {
      set({ error: handleAuthErrorMessage(error), isSubmitting: false });
    }
  },

  logout: () => {
    if (get().user?.globalRole === 'GUEST' && get().user?.id === 'guest-local') {
      try {
        const projectStore = useProjectStore.getState();
        projectStore.specifications?.forEach((spec) => projectStore.deleteSpecification?.(spec.id));
        projectStore.projects?.forEach((project) => projectStore.deleteProject?.(project.id));
        projectStore.clients?.forEach((client) => projectStore.deleteClient?.(client.id));
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('project-storage');
          window.localStorage.removeItem('sampleDataVersion');
        }
      } catch {
        // No-op: best-effort cleanup for guest mode.
      }
      set({
        user: null,
        token: null,
        refreshToken: null,
        mfaRequired: false,
        mfaChallengeId: null,
        mfaMessage: null,
        temporaryMfaCode: null,
        mfaExpiresAt: null,
        mfaAttemptsRemaining: null,
        mfaLockedUntil: null,
        mfaResendAvailableAt: null,
      });
      return;
    }
    void authRequest('/auth/logout', { method: 'POST', skipAuth: true }).catch(() => null);
    writeAuthToken(null);
    writeRefreshToken(null);
      set({
        user: null,
        token: null,
        refreshToken: null,
        mfaRequired: false,
        mfaChallengeId: null,
        mfaMessage: null,
        temporaryMfaCode: null,
        mfaExpiresAt: null,
        mfaAttemptsRemaining: null,
        mfaLockedUntil: null,
        mfaResendAvailableAt: null,
      });
  },

  updateProfile: async (payload) => {
    set({ isSubmitting: true, error: null });
    try {
      const { user } = await authRequest<{ user: RawAuthUser }>(
        '/auth/profile',
        { method: 'PATCH', body: JSON.stringify(payload) }
      );
      set({ user: normalizeAuthUser(user), isSubmitting: false });
    } catch (error) {
      set({ error: handleAuthErrorMessage(error), isSubmitting: false });
      throw error;
    }
  },

  uploadAvatar: async (file) => {
    set({ isSubmitting: true, error: null });
    try {
      const token = typeof localStorage !== 'undefined'
        ? (localStorage.getItem('auth_token') ?? null) : null;
      const formData = new FormData();
      formData.append('file', file);
      const BASE = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:4000/api';
      const res = await fetch(`${BASE}/auth/avatar`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { message?: string };
        throw new Error(err.message ?? 'Erreur upload');
      }
      const { user } = await res.json() as { user: RawAuthUser };
      set({ user: normalizeAuthUser(user), isSubmitting: false });
    } catch (error) {
      set({ error: handleAuthErrorMessage(error), isSubmitting: false });
      throw error;
    }
  },

  removeAvatar: async () => {
    set({ isSubmitting: true, error: null });
    try {
      const { user } = await authRequest<{ user: RawAuthUser }>(
        '/auth/avatar', { method: 'DELETE' }
      );
      set({ user: normalizeAuthUser(user), isSubmitting: false });
    } catch (error) {
      set({ error: handleAuthErrorMessage(error), isSubmitting: false });
      throw error;
    }
  },

  toggleMfa: async ({ enable, password }) => {
    set({ isSubmitting: true, error: null });
    try {
      const { user } = await authRequest<{ user: RawAuthUser }>(
        '/auth/mfa',
        { method: 'POST', body: JSON.stringify({ enable, password }) }
      );
      set({ user: normalizeAuthUser(user), isSubmitting: false });
    } catch (error) {
      set({ error: handleAuthErrorMessage(error), isSubmitting: false });
      throw error;
    }
  },

  changePassword: async ({ currentPassword, newPassword }) => {
    set({ isSubmitting: true, error: null });
    try {
      const { token, refreshToken, user } = await authRequest<{
        token: string;
        refreshToken: string;
        user: RawAuthUser;
      }>(
        '/auth/change-password',
        {
          method: 'POST',
          body: JSON.stringify({ currentPassword, newPassword }),
        }
      );
      writeAuthToken(token);
      writeRefreshToken(refreshToken);
      set({ user: normalizeAuthUser(user), token: null, refreshToken: null, isSubmitting: false });
    } catch (error) {
      set({ error: handleAuthErrorMessage(error), isSubmitting: false });
      throw error;
    }
  },

  /**
   * Atomic password reset flow:
   * 1. Login with temporary password to obtain a JWT (without updating store.user,
   *    to avoid rendering <ForcePasswordChange> as an intermediate state).
   * 2. Immediately call change-password with the new token.
   * 3. Only then set the final authenticated user state (passwordResetRequired=false).
   */
  resetPasswordWithTemp: async (email, tempPassword, newPassword) => {
    set({ isSubmitting: true, error: null });

    // Step 1 — authenticate with temp password, keep token private
    // Erreur distincte : si la 401 vient d'ici, c'est le mot de passe
    // temporaire qui est mauvais, pas les identifiants du compte.
    let loginToken: string;
    let loginRefreshToken: string;
    try {
      const loginResult = await authRequest<{
        token: string;
        refreshToken: string;
        user: RawAuthUser;
      }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password: tempPassword }),
        skipAuth: true,
      });
      if (!('token' in loginResult)) {
        throw new Error('Mot de passe temporaire invalide');
      }
      loginToken = loginResult.token;
      loginRefreshToken = loginResult.refreshToken;
    } catch {
      // L'échec ici signifie que le mot de passe temporaire est incorrect.
      const msg = 'Le mot de passe temporaire saisi est incorrect. Vérifiez votre email ou renvoyez-en un nouveau.';
      set({ error: msg, isSubmitting: false });
      throw new Error(msg);
    }

    // Step 2 — change to new password (authRequest reads token from localStorage)
    try {
      writeAuthToken(loginToken);
      writeRefreshToken(loginRefreshToken);
      const { token, refreshToken, user } = await authRequest<{
        token: string;
        refreshToken: string;
        user: RawAuthUser;
      }>('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword: tempPassword, newPassword }),
      });
      writeAuthToken(token);
      writeRefreshToken(refreshToken);
      set({ user: normalizeAuthUser(user), token: null, refreshToken: null, isSubmitting: false });
      await get().refreshUser();
    } catch (error) {
      // Clean up any partial token written during step 1
      writeAuthToken(null);
      writeRefreshToken(null);
      set({ error: handleAuthErrorMessage(error), isSubmitting: false });
      throw error;
    }
  },

  requestPasswordReset: async (payload) => {
    set({ isSubmitting: true, error: null });
    try {
      const result = await authRequest<{
        message: string;
        temporaryPassword: string | null;
        channel?: string;
        resendAvailableAt?: string;
      }>(
        '/auth/forgot-password',
        {
          method: 'POST',
          body: JSON.stringify(payload),
          skipAuth: true,
        }
      );
      set({ isSubmitting: false });
      return result;
    } catch (error) {
      set({ error: handleAuthErrorMessage(error), isSubmitting: false });
      throw error;
    }
  },

  verifyMfa: async ({ challengeId, code }) => {
    set({ isSubmitting: true, error: null });
    try {
      const { token, refreshToken, user, rememberToken } = await authRequest<{
        token: string;
        refreshToken: string;
        user: RawAuthUser;
        rememberToken?: string | null;
      }>(
        '/auth/verify-otp',
        {
          method: 'POST',
          body: JSON.stringify({ challengeId, code, rememberDevice: get().rememberDevice }),
          skipAuth: true,
        }
      );
      writeAuthToken(token);
      writeRefreshToken(refreshToken);
      if (rememberToken) {
        writeRememberToken(rememberToken);
      }
      set({
        user: normalizeAuthUser(user),
        token: null,
        refreshToken: null,
        isSubmitting: false,
        mfaRequired: false,
        mfaChallengeId: null,
        mfaMessage: null,
        temporaryMfaCode: null,
        mfaExpiresAt: null,
        mfaAttemptsRemaining: null,
        mfaLockedUntil: null,
        mfaResendAvailableAt: null,
        rememberDevice: false,
      });
      // Même raison que pour login() : /auth/verify-otp peut omettre
      // orgMemberRole/organizationId → on resynchronise via /auth/me.
      await get().refreshUser();
    } catch (error) {
      if (error instanceof Error) {
        const payload = (error as { payload?: any }).payload;
        if (payload && typeof payload === 'object') {
          set({
            mfaAttemptsRemaining:
              typeof payload.attemptsRemaining === 'number'
                ? payload.attemptsRemaining
                : null,
            mfaLockedUntil:
              typeof payload.lockedUntil === 'string' ? payload.lockedUntil : null,
            mfaExpiresAt:
              typeof payload.otpExpiresAt === 'string' ? payload.otpExpiresAt : null,
            mfaResendAvailableAt:
              typeof payload.resendAvailableAt === 'string'
                ? payload.resendAvailableAt
                : null,
          });
        }
      }
      set({ error: handleAuthErrorMessage(error), isSubmitting: false });
      throw error;
    }
  },

  resendMfa: async ({ challengeId }) => {
    set({ isSubmitting: true, error: null });
    try {
      const result = await authRequest<{
        message: string;
        otpExpiresAt?: string | null;
        attemptsRemaining?: number | null;
        lockedUntil?: string | null;
        resendAvailableAt?: string | null;
        temporaryCode?: string | null;
      }>('/auth/resend-otp', {
        method: 'POST',
        body: JSON.stringify({ challengeId }),
        skipAuth: true,
      });
      set({
        isSubmitting: false,
        mfaMessage: result.message ?? null,
        mfaExpiresAt: result.otpExpiresAt ?? null,
        mfaAttemptsRemaining:
          typeof result.attemptsRemaining === 'number' ? result.attemptsRemaining : null,
        mfaLockedUntil: result.lockedUntil ?? null,
        mfaResendAvailableAt: result.resendAvailableAt ?? null,
        temporaryMfaCode: result.temporaryCode ?? null,
      });
    } catch (error) {
      if (error instanceof Error) {
        const payload = (error as { payload?: any }).payload;
        if (payload && typeof payload === 'object') {
          set({
            mfaResendAvailableAt:
              typeof payload.resendAvailableAt === 'string'
                ? payload.resendAvailableAt
                : null,
          });
        }
      }
      set({ error: handleAuthErrorMessage(error), isSubmitting: false });
      throw error;
    }
  },

  setRememberDevice: (value) => set({ rememberDevice: value }),

  clearMfa: () =>
    set({
      mfaRequired: false,
      mfaChallengeId: null,
      mfaMessage: null,
      temporaryMfaCode: null,
      mfaExpiresAt: null,
      mfaAttemptsRemaining: null,
      mfaLockedUntil: null,
      mfaResendAvailableAt: null,
      rememberDevice: false,
    }),

  enterGuestMode: () => {
    const guestUser: AuthUser = {
      id: 'guest-local',
      email: 'invite@local',
      name: 'Invité',
      username: 'invite',
      avatarUrl: null,
      role: 'GUEST',
      globalRole: 'GUEST',
      passwordResetRequired: false,
      mfaEnabled: false,
      organizationId: null,
      orgMemberRole: null,
      orgMemberStatus: null,
    };
    writeAuthToken(null);
    writeRefreshToken(null);
    useProjectStore.getState().setStorageMode('local');
    set({
      user: guestUser,
      token: null,
      refreshToken: null,
      isInitializing: false,
      isSubmitting: false,
      error: null,
      mfaRequired: false,
      mfaChallengeId: null,
      mfaMessage: null,
      temporaryMfaCode: null,
      mfaExpiresAt: null,
      mfaAttemptsRemaining: null,
      mfaLockedUntil: null,
      mfaResendAvailableAt: null,
      rememberDevice: false,
    });
  },
}));
